import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/constants";

export default function LocalProof() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="relative overflow-hidden bg-secondary text-white grain-texture">
      {/* soft orange glow */}
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-primary font-bold text-sm tracking-widest">LOOK FOR OUR SIGN</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 leading-tight">
            You've Probably Seen Us <span className="text-primary">Around the Neighborhood</span>
          </h2>
          <p className="text-white/70 mt-5 text-lg leading-relaxed max-w-xl">
            When the CG Concrete sign goes up in a yard, quality work is happening nearby. From Watertown to Oconomowoc, our sign is a simple promise — solid concrete, poured right, backed by a local team.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <span className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> Licensed & Insured</span>
            <span className="flex items-center gap-2 text-sm font-semibold"><Star className="h-4 w-4 text-primary" /> Residential &amp; Commercial</span>
            <span className="flex items-center gap-2 text-sm font-semibold"><Star className="h-4 w-4 text-primary" /> Free Estimates</span>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild className="h-12 px-7 bg-primary hover:bg-primary/90 rounded-sm font-bold hover:-translate-y-1 transition-transform" data-testid="localproof-quote-btn">
              <Link to="/contact">Start Your Project <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-7 rounded-sm font-bold bg-transparent border-white/30 text-white hover:bg-white hover:text-secondary">
              <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>

        {/* Planted yard sign */}
        <div className="relative flex justify-center items-end min-h-[360px] sm:min-h-[440px]">
          {/* grassy mound */}
          <div className="absolute bottom-6 w-[85%] h-24 bg-gradient-to-t from-green-900 to-green-700 rounded-[100%] blur-[2px]" />
          <div className="absolute bottom-8 w-[68%] h-16 bg-green-700/40 rounded-[100%] blur-md" />
          {/* ground shadow */}
          <div className="absolute bottom-9 w-[52%] h-6 bg-black/55 rounded-[100%] blur-md" />
          <motion.img
            src="/archive/yard-sign.png"
            alt="CG Concrete LLC yard sign — Residential and Commercial — 920.253.5606"
            className="relative z-10 w-[78%] max-w-md object-contain drop-shadow-2xl origin-bottom"
            animate={reduceMotion ? {} : { rotate: [-1.2, 1.2, -1.2] }}
            transition={reduceMotion ? {} : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="yard-sign"
          />
        </div>
      </div>
    </section>
  );
}
